// src/oura/components/docs/threads/SummaryFold.tsx
//
// The AI summary of a doc (Docs phase 2, lane 4: 2.15), staff only and
// labelled AI. Folded, its head keeps the one line on where the doc stands.
// Open: up to 3 open points, possible mistakes in our own doc, the next step,
// and when it was made. It is made only when staff open a doc that changed
// since the last one, never in the background, and nothing reads it but this
// panel: every state, count and date on the drawer comes from the doc. The
// sheet runs it (useDocSummaryOnOpen) once per opening and hands it here, so
// coming back from a write view never makes a new one; "Make a new summary"
// asks in the drawer foot, since it is a paid call, then makes a fresh one. The monthly cost cap lives in the Skill library's
// settings, not on this working screen.

import { LightbulbIcon, RefreshCwIcon, SparklesIcon, TriangleAlertIcon } from "lucide-react";
import type { DocumentDetail, StaffDocument } from "@service/aeo/clientDocs";
import { cn } from "../../../lib/cn";
import { AREA_TEXT, DrawerFold, DrawerPulse, PillButton, TYPE, Tag } from "@oura/ui";
import { docsFailureText } from "@oura/docs";
import type { SummaryState } from "./model";

const TIME = new Intl.DateTimeFormat("en-GB", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Kuala_Lumpur" });

/** "3.13pm", the house time, in Malaysian time. */
function madeAtText(iso: string): string | null {
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return null;
    const parts = TIME.formatToParts(t);
    const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
    return `${part("hour")}.${part("minute")}${part("dayPeriod").replace(/\s|\./g, "").toLowerCase()}`;
}

/** "Tue 29 Sep" from a YYYY-MM-DD. */
const DAY = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
const dayText = (ymd: string) => DAY.format(new Date(`${ymd}T00:00:00Z`)).replace(",", "");

/** Today in Malaysia, YYYY-MM-DD. */
const myToday = () => new Date(Date.now() + 8 * 3_600_000).toISOString().slice(0, 10);

/**
 * The next step, from the same state the drawer's lists use (never the
 * summary's own words, which age): a reply that needs us, then a chase once
 * the reply-by has passed, then filing, then nothing.
 */
function nextFromState(detail: DocumentDetail, filed: boolean, redFlag: boolean, openPoints: boolean): string {
    const t = detail.threads;
    const needs = t.filter((x) => x.state === "needs_us").length;
    const waiting = t.filter((x) => x.kind === "question" && (x.state === "waiting_on_client" || x.state === "late")).length;
    // A red flag outranks every other step: never send a doc that has one to the client.
    if (redFlag) return "Fix the red item first.";
    if (!detail.sharing.clientCanSee) return "Read it, then share it with the client.";
    if (needs > 0) return needs === 1 ? "Reply to the answer that needs us." : `Reply to the ${needs} answers that need us.`;
    if (waiting > 0) {
        if (!detail.replyBy) return "Wait for the client's reply.";
        return detail.replyBy < myToday() ? `Chase: reply was due ${dayText(detail.replyBy)}.` : `Wait for the client's reply by ${dayText(detail.replyBy)}.`;
    }
    // The summary above lists open points, so "Nothing is open." would contradict it.
    if (openPoints) return "Decide the open points above.";
    if (detail.state === "approved") return filed ? "Nothing is open. The approved PDF is filed." : "File the approved PDF.";
    return "Nothing is open.";
}

export function SummaryFold({
    state: s,
    detail,
    filed = false,
    flags = [],
}: {
    state: SummaryState;
    detail: DocumentDetail | null;
    filed?: boolean;
    /** The doc's own flags. A red one (not "No code yet", which has its own button) changes the next step. */
    flags?: StaffDocument["flags"];
}) {
    const summary = s.summary;
    const stale = Boolean(summary && detail && summary.madeFrom !== detail.modifiedAt);
    const made = summary ? madeAtText(summary.madeAt) : null;
    const note = summary ? summary.line : s.status === "loading" ? "Reading the doc" : s.error ? docsFailureText(s.error, "the summary") : null;

    const label = (
        <span className="inline-flex items-center gap-1.5">
            <SparklesIcon aria-hidden size={14} className={cn("shrink-0", AREA_TEXT.ai)} />
            Summary
            <Tag tone="indigo">AI</Tag>
        </span>
    );

    return (
        <DrawerFold id="staff-doc.summary" label={label} note={note} remember>
            {!summary ? (
                s.status === "loading" ? (
                    <DrawerPulse lines={3} label="Making the summary" />
                ) : (
                    <p className="m-0 text-gray-700">{s.error ? docsFailureText(s.error, "the summary") : "No summary yet."}</p>
                )
            ) : (
                <div className="space-y-3">
                    <p className="m-0 break-words text-gray-900">{summary.line}</p>
                    {summary.openPoints.length > 0 && (
                        <div>
                            <p className={cn(TYPE.lab, "m-0")}>Open points</p>
                            <ul className="m-0 mt-1 list-disc space-y-1 pl-5 text-gray-700">
                                {summary.openPoints.slice(0, 3).map((p) => (
                                    <li key={p} className="break-words">
                                        {p}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {summary.mistakes.length > 0 && (
                        <div>
                            <p className={cn(TYPE.lab, "m-0")}>Possible mistakes in our doc</p>
                            <ul className="m-0 mt-1 list-plain space-y-1 p-0 text-gray-700">
                                {summary.mistakes.map((m) => (
                                    <li key={m} className="flex items-start gap-2">
                                        <TriangleAlertIcon aria-hidden size={14} className="mt-[3px] shrink-0 text-amber-600" />
                                        <span className="min-w-0 break-words">{m}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <p className="m-0 flex items-start gap-2 text-gray-900">
                        <LightbulbIcon aria-hidden size={14} className={cn("mt-[3px] shrink-0", AREA_TEXT.dates)} />
                        <span className="min-w-0 break-words">
                            <span className="font-medium">Next: </span>
                            {detail
                                ? nextFromState(
                                      detail,
                                      filed,
                                      flags.some((f) => f.tone === "red" && f.code !== "no_code"),
                                      summary.openPoints.length > 0,
                                  )
                                : summary.nextStep}
                        </span>
                    </p>
                    <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                        {made ? `Made when you opened it, ${made}.` : "Made when you opened it."}
                        {stale && " The doc has changed since."}
                        {s.error && ` ${docsFailureText(s.error, "the summary")}`}
                    </p>
                    {s.remake && (
                        <PillButton size="sm" icon={RefreshCwIcon} busy={s.remaking} writes onClick={s.remake}>
                            Make a new summary
                        </PillButton>
                    )}
                </div>
            )}
        </DrawerFold>
    );
}
